<template>
  <div class="banner-management">
    <!-- 页面标题 -->
    <div class="page-header">
      <h2 class="page-title">
        <i class="el-icon-picture"></i>
        轮播图管理
      </h2>
      <p class="page-description">管理新文科模块的轮播图内容</p>
    </div>

    <!-- 搜索和操作区域 -->
    <div class="toolbar">
      <div class="search-section">
        <el-input
          v-model="searchForm.keyword"
          placeholder="搜索轮播图名称"
          class="search-input"
          clearable
          @keyup.enter="handleSearch"
        >
          <el-button slot="append" icon="el-icon-search" @click="handleSearch"></el-button>
        </el-input>
        
        <el-select v-model="searchForm.state" placeholder="状态筛选" clearable class="state-select">
          <el-option label="全部" value=""></el-option>
          <el-option label="启用" value="1"></el-option>
          <el-option label="禁用" value="0"></el-option>
        </el-select>
        
        <el-button type="primary" @click="handleSearch">
          <i class="el-icon-search"></i>
          搜索
        </el-button>
        <el-button @click="handleReset">
          <i class="el-icon-refresh"></i>
          重置
        </el-button>
      </div>
      
      <div class="action-section">
        <el-button type="primary" @click="handleAdd">
          <i class="el-icon-plus"></i>
          新增轮播图
        </el-button>
        <el-button 
          type="warning" 
          :disabled="selectedRows.length === 0"
          @click="handleBatchEnable"
        >
          <i class="el-icon-check"></i>
          批量启用
        </el-button>
        <el-button 
          type="info" 
          :disabled="selectedRows.length === 0"
          @click="handleBatchDisable"
        >
          <i class="el-icon-close"></i>
          批量禁用
        </el-button>
      </div>
    </div>

    <!-- 数据表格 -->
    <div class="table-container">
      <el-table
        :data="tableData"
        v-loading="loading"
        @selection-change="handleSelectionChange"
        stripe
        border
        style="width: 100%"
      >
        <el-table-column type="selection" width="55" align="center" header-align="center"></el-table-column>
        
        <el-table-column prop="id" label="ID" width="80" align="center" header-align="center"></el-table-column>
        
        <el-table-column label="轮播图" width="120" align="center" header-align="center">
          <template slot-scope="scope">
            <div class="banner-image">
              <img 
                :src="scope.row.image" 
                :alt="scope.row.name"
                @error="handleImageError"
                @click="previewImage(scope.row.image)"
              />
            </div>
          </template>
        </el-table-column>
        
        <el-table-column prop="name" label="名称" min-width="180" show-overflow-tooltip header-align="center"></el-table-column>
        
        <el-table-column label="类型" width="120" align="center" header-align="center">
          <template slot-scope="scope">
            <el-tag :type="scope.row.type === 1 ? 'primary' : 'success'" size="small">
              {{ scope.row.type === 1 ? '独立图片' : '内联链接' }}
            </el-tag>
          </template>
        </el-table-column>
        
        <el-table-column prop="url" label="跳转链接" min-width="250" show-overflow-tooltip header-align="center">
          <template slot-scope="scope">
            <span v-if="scope.row.url">{{ scope.row.url }}</span>
            <span v-else class="text-muted">无</span>
          </template>
        </el-table-column>
        
        <el-table-column label="状态" width="100" align="center" header-align="center">
          <template slot-scope="scope">
            <el-switch
              v-model="scope.row.state"
              :active-value="1"
              :inactive-value="0"
              @change="handleStatusChange(scope.row)"
            ></el-switch>
          </template>
        </el-table-column>
        
        <el-table-column prop="createTime" label="创建时间" width="180" align="center" header-align="center">
          <template slot-scope="scope">
            {{ formatDate(scope.row.createTime) }}
          </template>
        </el-table-column>
        
        <el-table-column label="操作" width="180" align="center" header-align="center" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" @click="handleEdit(scope.row)">
              <i class="el-icon-edit"></i>
              编辑
            </el-button>
            <el-button size="mini" type="danger" @click="handleDelete(scope.row)">
              <i class="el-icon-delete"></i>
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 分页 -->
    <div class="pagination-container">
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="pagination.pageNum"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="pagination.pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="pagination.total"
      ></el-pagination>
    </div>

    <!-- 新增/编辑对话框 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="600px"
      @close="handleDialogClose"
    >
      <el-form
        ref="bannerForm"
        :model="bannerForm"
        :rules="bannerRules"
        label-width="100px"
      >
        <el-form-item label="轮播图名称" prop="name">
          <el-input v-model="bannerForm.name" placeholder="请输入轮播图名称"></el-input>
        </el-form-item>
        
        <el-form-item label="轮播图类型" prop="type">
          <el-radio-group v-model="bannerForm.type">
            <el-radio :label="1">独立图片</el-radio>
            <el-radio :label="2">内联链接</el-radio>
          </el-radio-group>
        </el-form-item>
        
        <el-form-item label="轮播图图片" prop="image">
          <el-input v-model="bannerForm.image" placeholder="请输入图片URL">
            <el-button slot="append" @click="previewImage(bannerForm.image)">预览</el-button>
          </el-input>
        </el-form-item>
        
        <el-form-item label="跳转链接" prop="url">
          <el-input v-model="bannerForm.url" placeholder="请输入跳转链接（可选）"></el-input>
        </el-form-item>
        
        <el-form-item label="状态" prop="state">
          <el-radio-group v-model="bannerForm.state">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">禁用</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit" :loading="submitLoading">
          {{ isEdit ? '更新' : '创建' }}
        </el-button>
      </div>
    </el-dialog>

    <!-- 图片预览对话框 -->
    <el-dialog
      title="图片预览"
      :visible.sync="previewVisible"
      width="auto"
      center
    >
      <div class="image-preview">
        <img :src="previewImageUrl" alt="预览图片" style="max-width: 100%; max-height: 500px;" />
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { bannerApi } from '@/api/api';

export default {
  name: 'BannerManagement',
  data() {
    return {
      loading: false,
      submitLoading: false,
      dialogVisible: false,
      previewVisible: false,
      isEdit: false,
      dialogTitle: '新增轮播图',
      previewImageUrl: '',
      selectedRows: [],
      
      // 搜索表单
      searchForm: {
        keyword: '',
        state: ''
      },
      
      // 分页数据
      pagination: {
        pageNum: 1,
        pageSize: 10,
        total: 0
      },
      
      // 表格数据
      tableData: [],
      
      // 表单数据
      bannerForm: {
        id: null,
        name: '',
        type: 1,
        image: '',
        url: '',
        state: 1
      },
      
      // 表单验证规则
      bannerRules: {
        name: [
          { required: true, message: '请输入轮播图名称', trigger: 'blur' },
          { min: 1, max: 50, message: '名称长度在 1 到 50 个字符', trigger: 'blur' }
        ],
        type: [
          { required: true, message: '请选择轮播图类型', trigger: 'change' }
        ],
        image: [
          { required: true, message: '请输入图片URL', trigger: 'blur' },
          { type: 'url', message: '请输入正确的URL格式', trigger: 'blur' }
        ],
        url: [
          { type: 'url', message: '请输入正确的URL格式', trigger: 'blur' }
        ],
        state: [
          { required: true, message: '请选择状态', trigger: 'change' }
        ]
      }
    };
  },
  
  mounted() {
    // 检查管理员权限
    this.checkAdminPermission();
    // 页面加载时刷新管理员状态
    this.loadData();
  },
  
  methods: {
    // 检查管理员权限
    checkAdminPermission() {
      const adminToken = localStorage.getItem('admin_token');
      if (!adminToken) {
        this.$message.error('请先登录管理员账号');
        this.$router.push({ name: 'AdminLogin' });
        return false;
      }
      return true;
    },
    
    // 加载数据
    async loadData() {
      this.loading = true;
      try {
        const params = {
          pageNum: this.pagination.pageNum,
          pageSize: this.pagination.pageSize
        };
        
        // 只添加非空的搜索参数
        if (this.searchForm.keyword && this.searchForm.keyword.trim()) {
          params.keyword = this.searchForm.keyword.trim();
        }
        if (this.searchForm.state !== '' && this.searchForm.state !== null && this.searchForm.state !== undefined) {
          params.state = this.searchForm.state;
        }
        
        const response = await bannerApi.getBannerList(params);
        
        // 检查响应是否成功
        if (response.data && (response.data.success === true || response.data.code === 200)) {
          // 使用response.data中的数据
          const data = response.data;
          this.tableData = data.list || data.records || [];
          this.pagination.total = data.total || 0;
          
          // 如果数据为空，显示提示
          if (this.tableData.length === 0) {
            this.$message.info('暂无轮播图数据，请先添加轮播图');
          }
        } else if (response.success === true || response.code === 200) {
          // 兼容直接在response中的数据
          const data = response.data || response;
          this.tableData = data.list || data.records || [];
          this.pagination.total = data.total || 0;
          
          // 如果数据为空，显示提示
          if (this.tableData.length === 0) {
            this.$message.info('暂无轮播图数据，请先添加轮播图');
          }
        } else {
          this.$message.error(response.message || response.msg || '获取数据失败');
        }
      } catch (error) {
        console.error('加载数据失败:', error);
        console.error('错误详情:', error.response?.data || error.message);
        this.$message.error(`加载数据失败: ${error.response?.data?.message || error.message || '未知错误'}`);
      } finally {
        this.loading = false;
      }
    },
    
    // 搜索
    handleSearch() {
      this.pagination.pageNum = 1;
      this.loadData();
    },
    
    // 重置搜索
    handleReset() {
      this.searchForm = {
        keyword: '',
        state: ''
      };
      this.pagination.pageNum = 1;
      this.loadData();
    },
    
    // 新增
    handleAdd() {
      this.isEdit = false;
      this.dialogTitle = '新增轮播图';
      this.bannerForm = {
        id: null,
        name: '',
        type: 1,
        image: '',
        url: '',
        state: 1
      };
      this.dialogVisible = true;
    },
    
    // 编辑
    handleEdit(row) {
      this.isEdit = true;
      this.dialogTitle = '编辑轮播图';
      this.bannerForm = {
        id: row.id,
        name: row.name,
        type: row.type,
        image: row.image,
        url: row.url || '',
        state: row.state
      };
      this.dialogVisible = true;
    },
    
    // 删除
    handleDelete(row) {
      this.$confirm(`确定要删除轮播图"${row.name}"吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const response = await bannerApi.deleteBanner(row.id);
          if (response.code === 200 || response.success === true) {
            this.$message.success(response.message || response.msg || '删除成功');
            this.loadData();
          } else {
            this.$message.error(response.message || response.msg || '删除失败');
          }
        } catch (error) {
          console.error('删除失败:', error);
          this.$message.error('删除失败');
        }
      });
    },
    
    // 状态切换
    async handleStatusChange(row) {
      try {
        const response = await bannerApi.updateBanner({
          id: row.id,
          name: row.name,
          type: row.type,
          image: row.image,
          url: row.url,
          state: row.state
        });
        
        if (response.code === 200 || response.success === true) {
          this.$message.success(response.message || response.msg || '状态更新成功');
        } else {
          this.$message.error(response.message || response.msg || '状态更新失败');
          // 恢复原状态
          row.state = row.state === 1 ? 0 : 1;
        }
      } catch (error) {
        console.error('状态更新失败:', error);
        this.$message.error('状态更新失败');
        // 恢复原状态
        row.state = row.state === 1 ? 0 : 1;
      }
    },
    
    // 批量启用
    handleBatchEnable() {
      this.batchUpdateState(1);
    },
    
    // 批量禁用
    handleBatchDisable() {
      this.batchUpdateState(0);
    },
    
    // 批量更新状态
    async batchUpdateState(state) {
      if (this.selectedRows.length === 0) {
        this.$message.warning('请选择要操作的数据');
        return;
      }
      
      const action = state === 1 ? '启用' : '禁用';
      this.$confirm(`确定要${action}选中的 ${this.selectedRows.length} 个轮播图吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const ids = this.selectedRows.map(row => row.id);
          const response = await bannerApi.batchUpdateState({ ids, state });
          
          if (response.code === 200 || response.success === true) {
            this.$message.success(response.message || response.msg || `批量${action}成功`);
            this.loadData();
          } else {
            this.$message.error(response.message || response.msg || `批量${action}失败`);
          }
        } catch (error) {
          console.error(`批量${action}失败:`, error);
          this.$message.error(`批量${action}失败`);
        }
      });
    },
    
    // 提交表单
    handleSubmit() {
      this.$refs.bannerForm.validate(async (valid) => {
        if (valid) {
          this.submitLoading = true;
          try {
            let response;
            if (this.isEdit) {
              response = await bannerApi.updateBanner(this.bannerForm);
            } else {
              response = await bannerApi.createBanner(this.bannerForm);
            }
            
            if (response.code === 200 || response.success === true) {
              this.$message.success(response.message || response.msg || (this.isEdit ? '更新成功' : '创建成功'));
              this.dialogVisible = false;
              this.loadData();
            } else {
              this.$message.error(response.message || response.msg || (this.isEdit ? '更新失败' : '创建失败'));
            }
          } catch (error) {
            console.error('提交失败:', error);
            this.$message.error(this.isEdit ? '更新失败' : '创建失败');
          } finally {
            this.submitLoading = false;
          }
        }
      });
    },
    
    // 对话框关闭
    handleDialogClose() {
      this.$refs.bannerForm.resetFields();
    },
    
    // 表格选择变化
    handleSelectionChange(selection) {
      this.selectedRows = selection;
    },
    
    // 分页大小变化
    handleSizeChange(val) {
      this.pagination.pageSize = val;
      this.pagination.pageNum = 1;
      this.loadData();
    },
    
    // 当前页变化
    handleCurrentChange(val) {
      this.pagination.pageNum = val;
      this.loadData();
    },
    
    // 图片预览
    previewImage(url) {
      if (!url) {
        this.$message.warning('图片URL为空');
        return;
      }
      this.previewImageUrl = url;
      this.previewVisible = true;
    },
    
    // 图片加载错误
    handleImageError(event) {
      event.target.src = require('@/assets/zanwu.png');
    },
    
    // 格式化日期
    formatDate(dateStr) {
      if (!dateStr) return '-';
      const date = new Date(dateStr);
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  }
};
</script>

<style scoped>
.banner-management {
  padding: 0;
}

.page-header {
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  color: #222;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
}

.page-title i {
  margin-right: 12px;
  color: #f1787f;
}

.page-description {
  color: #666;
  margin: 0;
  font-size: 14px;
}

.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 20px;
  background: #fafbfc;
  border-radius: 8px;
  border: 1px solid #f0f0f0;
}

.search-section {
  display: flex;
  align-items: center;
  gap: 12px;
}

.search-input {
  width: 280px;
}

.state-select {
  width: 120px;
}

.action-section {
  display: flex;
  gap: 12px;
}

.table-container {
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
}

/* 表格标题居中样式 */
.table-container .el-table th {
  text-align: center !important;
  font-weight: 600;
  background-color: #fafbfc;
}

/* 表格内容样式优化 */
.table-container .el-table td {
  padding: 12px 0;
}

/* 操作按钮样式优化 */
.table-container .el-button--mini {
  padding: 5px 8px;
  margin: 0 2px;
}

/* 状态开关样式 */
.table-container .el-switch {
  transform: scale(0.9);
}

.banner-image {
  width: 80px;
  height: 50px;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s;
}

.banner-image:hover {
  transform: scale(1.05);
}

.banner-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.text-muted {
  color: #999;
}

.pagination-container {
  margin-top: 20px;
  text-align: right;
}

.dialog-footer {
  text-align: right;
}

.image-preview {
  text-align: center;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .toolbar {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
  }
  
  .search-section {
    flex-direction: column;
    gap: 12px;
  }
  
  .search-input {
    width: 100%;
  }
  
  .action-section {
    justify-content: center;
    flex-wrap: wrap;
  }
  
  .page-title {
    font-size: 20px;
  }
}
</style>
